import React, { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import DirectionsIcon from '@mui/icons-material/Directions';
import PlaceIcon from '@mui/icons-material/Place';

const address =
  'Başpınar OSB Mah. OSB 1. Bölge 83101 Nolu Cadde No:17, İpek İş Merkezi B Blok No:9, Şehitkamil / Gaziantep';
const mapCoordinates = '37.147578,37.292962';

const roadmapSteps = [
  'Konum doğrulama',
  'Rota planı',
  'Ofis ziyareti',
  'Süreç başlangıcı',
];

const RoadmapCanvas = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) {
      return undefined;
    }

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    camera.position.set(0, 5.4, 10.5);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.setClearColor(0x050816, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    mount.appendChild(renderer.domElement);

    const group = new THREE.Group();
    group.rotation.x = -0.18;
    scene.add(group);

    const curve = new THREE.CatmullRomCurve3(
      [
        new THREE.Vector3(-6.6, 0, -1.2),
        new THREE.Vector3(-4.2, 0.15, 1.35),
        new THREE.Vector3(-1.3, 0, -0.8),
        new THREE.Vector3(1.4, 0.2, 1.18),
        new THREE.Vector3(4.2, 0.04, -0.55),
        new THREE.Vector3(6.4, 0.12, 0.85),
      ],
      false,
      'centripetal',
      0.45
    );

    const roadMaterial = new THREE.MeshStandardMaterial({
      color: 0x05111f,
      emissive: 0x035b7a,
      emissiveIntensity: 0.42,
      roughness: 0.4,
      metalness: 0.35,
    });
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0x22d3ee,
      transparent: true,
      opacity: 0.28,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const road = new THREE.Mesh(new THREE.TubeGeometry(curve, 180, 0.085, 14, false), roadMaterial);
    const glow = new THREE.Mesh(new THREE.TubeGeometry(curve, 180, 0.19, 18, false), glowMaterial);
    group.add(glow, road);

    const grid = new THREE.GridHelper(14, 28, 0x164e63, 0x0f2f48);
    grid.position.y = -0.42;
    grid.material.transparent = true;
    grid.material.opacity = 0.22;
    group.add(grid);

    const markerMaterial = new THREE.MeshStandardMaterial({
      color: 0x0ea5e9,
      emissive: 0x22d3ee,
      emissiveIntensity: 0.9,
      roughness: 0.25,
    });
    const markerGlowMaterial = new THREE.MeshBasicMaterial({
      color: 0x67e8f9,
      transparent: true,
      opacity: 0.18,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const markers = [0.03, 0.32, 0.61, 0.88].map((position, index) => {
      const point = curve.getPointAt(position);
      const marker = new THREE.Group();
      marker.position.copy(point);

      const column = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 0.9, 16), markerMaterial);
      column.position.y = 0.45;

      const core = new THREE.Mesh(new THREE.SphereGeometry(0.17, 24, 24), markerMaterial);
      core.position.y = 0.94;

      const halo = new THREE.Mesh(new THREE.SphereGeometry(0.44, 24, 24), markerGlowMaterial);
      halo.position.y = 0.94;

      marker.add(column, halo, core);
      marker.userData.phase = index * 0.85;
      group.add(marker);
      return marker;
    });

    const pulse = new THREE.Mesh(
      new THREE.SphereGeometry(0.16, 28, 28),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.9,
        blending: THREE.AdditiveBlending,
      })
    );
    group.add(pulse);

    const particleCount = 90;
    const particlePositions = new Float32Array(particleCount * 3);
    for (let index = 0; index < particleCount; index += 1) {
      particlePositions[index * 3] = (Math.random() - 0.5) * 15;
      particlePositions[index * 3 + 1] = Math.random() * 2.5 + 0.2;
      particlePositions[index * 3 + 2] = (Math.random() - 0.5) * 6;
    }
    const particleGeometry = new THREE.BufferGeometry();
    particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
    const particles = new THREE.Points(
      particleGeometry,
      new THREE.PointsMaterial({
        color: 0x67e8f9,
        size: 0.045,
        transparent: true,
        opacity: 0.42,
        blending: THREE.AdditiveBlending,
      })
    );
    group.add(particles);

    scene.add(new THREE.AmbientLight(0x8fe1ff, 0.65));
    const keyLight = new THREE.PointLight(0x22d3ee, 18, 18);
    keyLight.position.set(0, 4, 4);
    scene.add(keyLight);

    const pointer = { x: 0, y: 0 };
    const handlePointerMove = (event) => {
      const rect = mount.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
      pointer.y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    };
    mount.addEventListener('pointermove', handlePointerMove);

    const resize = () => {
      const { width, height } = mount.getBoundingClientRect();
      if (!width || !height) {
        return;
      }
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height, false);
      renderer.domElement.style.width = '100%';
      renderer.domElement.style.height = '100%';
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(mount);
    resize();

    const clock = new THREE.Clock();
    renderer.setAnimationLoop(() => {
      const elapsed = clock.getElapsedTime();
      const pulsePoint = curve.getPointAt((elapsed * 0.12) % 1);
      pulse.position.copy(pulsePoint);
      pulse.position.y += 0.22 + Math.sin(elapsed * 3) * 0.05;

      markers.forEach((marker) => {
        const scale = 1 + Math.sin(elapsed * 2.1 + marker.userData.phase) * 0.08;
        marker.scale.set(scale, scale, scale);
      });

      particles.rotation.y = elapsed * 0.025;
      group.rotation.y += (pointer.x * 0.07 - group.rotation.y) * 0.04;
      group.rotation.z += (-pointer.y * 0.025 - group.rotation.z) * 0.04;
      renderer.render(scene, camera);
    });

    return () => {
      renderer.setAnimationLoop(null);
      resizeObserver.disconnect();
      mount.removeEventListener('pointermove', handlePointerMove);
      if (renderer.domElement.parentElement === mount) {
        mount.removeChild(renderer.domElement);
      }

      scene.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach((material) => material.dispose());
        }
      });
      renderer.dispose();
    };
  }, []);

  return (
    <Box
      ref={mountRef}
      aria-hidden="true"
      sx={{
        position: 'absolute',
        inset: 0,
        opacity: { xs: 0.28, md: 0.46 },
        pointerEvents: 'none',
        '& canvas': {
          display: 'block',
        },
      }}
    />
  );
};

const LocationRoadmap = () => {
  const encodedCoordinates = useMemo(() => encodeURIComponent(mapCoordinates), []);
  const mapSrc = `https://www.google.com/maps?q=${encodedCoordinates}&output=embed`;
  const directionsHref = `https://www.google.com/maps/search/?api=1&query=${encodedCoordinates}`;

  return (
    <Box
      sx={{
        mb: 7,
        p: { xs: 2, md: 2.5 },
        minHeight: { xs: 480, md: 330 },
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 6,
        border: '1px solid rgba(89, 208, 255, 0.22)',
        boxShadow: '0 28px 80px rgba(2, 6, 23, 0.46)',
        background:
          'linear-gradient(135deg, rgba(7,17,31,0.94) 0%, rgba(5,10,22,0.98) 56%, rgba(8,20,35,0.94) 100%)',
      }}
    >
      <RoadmapCanvas />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 15% 20%, rgba(34,211,238,0.14) 0%, transparent 30%), linear-gradient(90deg, rgba(5,8,22,0.9) 0%, rgba(5,8,22,0.45) 46%, rgba(5,8,22,0.86) 100%)',
          pointerEvents: 'none',
        }}
      />

      <Grid container spacing={{ xs: 2.5, md: 3 }} alignItems="stretch" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid item xs={12} md={4}>
          <Stack
            spacing={2}
            sx={{
              height: '100%',
              minHeight: { md: 282 },
              p: { xs: 1, md: 1.5 },
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: 'primary.main',
                  fontSize: '0.82rem',
                  fontWeight: 800,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                  mb: 1,
                }}
              >
                Adres ve Konum
              </Typography>
              <Typography
                component="h3"
                sx={{
                  color: 'text.primary',
                  fontFamily: '"General Sans", "Inter", sans-serif',
                  fontSize: { xs: '1.8rem', md: '2.15rem' },
                  lineHeight: 1.08,
                  fontWeight: 800,
                  mb: 1.5,
                }}
              >
                Google Maps üzerinde bizi bulun
              </Typography>
              <Typography color="text.secondary" sx={{ lineHeight: 1.7, fontSize: '0.96rem' }}>
                Başpınar OSB içerisindeki ofis konumumuz için haritayı kullanabilir veya tek
                tıkla rota alabilirsiniz.
              </Typography>
            </Box>

            <Grid container spacing={1}>
              {roadmapSteps.map((step, index) => (
                <Grid item xs={6} key={step}>
                  <Stack
                    direction="row"
                    spacing={0.9}
                    alignItems="center"
                    sx={{
                      px: 1.1,
                      py: 0.95,
                      borderRadius: 3,
                      border: '1px solid rgba(148, 163, 184, 0.14)',
                      bgcolor: 'rgba(255,255,255,0.045)',
                      backdropFilter: 'blur(14px)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        color: '#06111f',
                        bgcolor: 'primary.main',
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography
                      sx={{
                        color: 'rgba(248, 251, 255, 0.84)',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        lineHeight: 1.25,
                      }}
                    >
                      {step}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Grid>

        <Grid item xs={12} md={8}>
          <Box
            sx={{
              height: '100%',
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid rgba(89, 208, 255, 0.24)',
              bgcolor: 'rgba(6, 17, 31, 0.86)',
              boxShadow: '0 24px 70px rgba(2, 6, 23, 0.42)',
              backdropFilter: 'blur(18px)',
            }}
          >
            <Box
              sx={{
                px: { xs: 1.6, md: 2.2 },
                py: { xs: 1.5, md: 1.6 },
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.3,
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                borderBottom: '1px solid rgba(148, 163, 184, 0.14)',
              }}
            >
              <Stack direction="row" spacing={1.15} alignItems="flex-start">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'rgba(89, 208, 255, 0.12)',
                    border: '1px solid rgba(89, 208, 255, 0.22)',
                    color: 'primary.main',
                    flexShrink: 0,
                  }}
                >
                  <PlaceIcon />
                </Box>
                <Box>
                  <Typography sx={{ color: 'text.primary', fontWeight: 800 }}>
                    GNC Veri Teknolojileri
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: '0.86rem', lineHeight: 1.5 }}>
                    {address}
                  </Typography>
                </Box>
              </Stack>

              <Button
                variant="contained"
                size="small"
                href={directionsHref}
                target="_blank"
                rel="noreferrer"
                startIcon={<DirectionsIcon />}
                sx={{
                  flexShrink: 0,
                  color: '#06111f',
                  background: 'linear-gradient(180deg, #ffffff 0%, #dff6ff 100%)',
                }}
              >
                Rota Al
              </Button>
            </Box>

            <Box
              sx={{
                position: 'relative',
                height: { xs: 210, sm: 230, md: 230 },
                maxHeight: { xs: 210, md: 230 },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.04)',
                },
              }}
            >
              <Box
                component="iframe"
                title="GNC Veri Teknolojileri Google Maps konumu"
                src={mapSrc}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                sx={{
                  border: 0,
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  filter: 'saturate(0.85) contrast(1.04)',
                }}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LocationRoadmap;
